import { Injectable } from '@angular/core';
import { FilterParser } from '@core/filter-parser';
import { PageFilter } from '@core/model/pagination/filter';
import { PageResult } from '@core/model/pagination/page-result';
import { LocalStorageDataService } from '@core/services/local-storage.service';
import { ObjectUtils, strictMap } from '@core/utils/object-utils';
import { TicketLaborFilterInput, GetTicketLaborByWorkerGQL, GetTicketLaborsGQL, AddTicketLaborGQL, AddTicketWorkerGQL, UpdateTicketLaborGQL, UpdateBillableGQL, UpdateEstimatedWorkingHoursGQL, UpdateRegularOverTimeHoursGQL, UpdateWorkingHoursGQL, RemoveWorkerGQL, LaborWorkingHoursGQL, UpdateTicketLaborGridGQL, AddLaborGQL, GetAllEmployeesSalariesGQL, GetTicketLaborByTicketIdsGQL } from '@generated/queries';
import { plainToInstance } from 'class-transformer';
import { firstValueFrom, map } from 'rxjs';
import { CreateLabor, CreateTicketLabor, TicketLaborGridUpdate, UpdateTicketLabor } from '../models/ticket-labor';
import {TicketLabor} from '../models/ticket-labor';
import { Salary } from '@core/model/employee/salary';


@Injectable({
  providedIn: 'root',
})
export class TicketLabourService {
  pageFilter: PageFilter = {
    pageIndex: 0,
    activeIndex: 1,
    take: 10,
    search: '',
    state: '',
  };
  vmFilters: TicketLaborFilterInput;

  constructor(
    private localStorageService: LocalStorageDataService,
    private paginatedTicketLaborByWorker: GetTicketLaborByWorkerGQL,
    private paginatedTicketLabors: GetTicketLaborsGQL,
    private ticketLaborsByIds: GetTicketLaborByTicketIdsGQL,
    private ticketLaborAdd: AddTicketLaborGQL,
    private ticketLaborAddWorker: AddTicketWorkerGQL,
    private LaborAdd: AddLaborGQL,
    private ticketLaborUpdate: UpdateTicketLaborGQL,
    private ticketLaborBillableUpdate: UpdateBillableGQL,
    private ticketLaborEstimatedUpdate: UpdateEstimatedWorkingHoursGQL,
    private ticketRegularOverTimeHours: UpdateRegularOverTimeHoursGQL,
    private ticketLaborWorkingHoursUpdate: UpdateWorkingHoursGQL,
    private ticketLaborHoursUpdate: UpdateTicketLaborGridGQL,
    private ticketLaborRemove: RemoveWorkerGQL,
    private laborWorkingHourRate: LaborWorkingHoursGQL,
    private employeesSalaries: GetAllEmployeesSalariesGQL
  ) {}

  getPaginatedTicketLaborsByWorker(
    filter: PageFilter,
    ticketId: string,
    workerId: string
  ): Promise<PageResult<TicketLabor>> {
    this.vmFilters = {};
    const filters = FilterParser.parseFilter({
      state: filter.state,
    });
    if (!ObjectUtils.isObjEmpty(filters)) {
      this.vmFilters = {
        and: [filters],
      };
    }
    const paginationFilter = FilterParser.parsePageFilter(filter);
    return firstValueFrom(
      this.paginatedTicketLaborByWorker
        .fetch(
          {
            ticketId: ticketId,
            workerId: workerId,
            skip: paginationFilter.skip,
            take: paginationFilter.take,
          },
          {
            fetchPolicy: 'network-only',
          }
        )
        .pipe(
          map(
            (x) =>
              new PageResult<TicketLabor>(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                x.data.supportTicket.ticketLabor as any
              )
          )
        )
    );
  }

  getPaginatedTicketLabors(
    filter: PageFilter,
    ticketId: string
  ): Promise<PageResult<TicketLabor>> {
    this.vmFilters = {};
    const filters = FilterParser.parseFilter({
      state: filter.state,
    });
    if (!ObjectUtils.isObjEmpty(filters)) {
      this.vmFilters = {
        and: [filters],
      };
    }
    const paginationFilter = FilterParser.parsePageFilter(filter);
    return firstValueFrom(
      this.paginatedTicketLabors
        .fetch(
          {
            ticketId: ticketId,
            skip: paginationFilter.skip,
            take: paginationFilter.take,
          },
          {
            fetchPolicy: 'network-only',
          }
        )
        .pipe(
          map(
            (x) =>
              new PageResult<TicketLabor>(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                x.data.supportTicket.ticketLabor as any
              )
          )
        )
    );
  }

  getTicketLaborsByTicketIds(ticketIds: string[]): Promise<TicketLabor[]> {
    return firstValueFrom(
      this.ticketLaborsByIds
        .fetch({ ticketIds }, { fetchPolicy: 'network-only' })
        .pipe(
          map((x) =>
            plainToInstance(
              TicketLabor,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              x.data.supportTicket.ticketLaborByTicketIds as any[]
            )
          )
        )
    );
  }

  getLaborWorkingHourRate(ticketId: string): Promise<Salary[]> {
    return firstValueFrom(
      this.laborWorkingHourRate
        .fetch(
          {
            ticketId,
          },
          { fetchPolicy: this.localStorageService.getFetchPolicy('employee') }
        )
        .pipe(map((x) => plainToInstance(Salary, x.data.hr.salariesForLabor)))
    );
  }

  getEmployeesSalaries(): Promise<Salary[]> {
    return firstValueFrom(
      this.employeesSalaries
        .fetch({}, { fetchPolicy: 'network-only' })
        .pipe(
          map((x) => plainToInstance(Salary, x.data.hr.salariesForAllEmployees))
        )
    );
  }

  addticketLabor(model: TicketLabor): Promise<string> {
    return firstValueFrom(
      this.ticketLaborAdd
        .mutate({ model: strictMap(CreateTicketLabor, model) })
        .pipe(map((x) => x.data.supportTicket.ticketLabor.add))
    );
  }

  addticketWorker(model: TicketLabor): Promise<string> {
    return firstValueFrom(
      this.ticketLaborAddWorker
        .mutate({ model: strictMap(CreateTicketLabor, model) })
        .pipe(map((x) => x.data.supportTicket.ticketLabor.addWorker))
    );
  }

  addLabor(model: CreateLabor): Promise<string> {
    return firstValueFrom(
      this.LaborAdd.mutate({ model: strictMap(CreateLabor, model) }).pipe(
        map((x) => x.data.supportTicket.ticketLabor.addLabor)
      )
    );
  }

  updateTicketLabor(model: TicketLabor): Promise<string> {
    return firstValueFrom(
      this.ticketLaborUpdate
        .mutate({ model: strictMap(UpdateTicketLabor, model) })
        .pipe(map((x) => x.data.supportTicket.ticketLabor.update))
    );
  }

  updateTicketLaborBillable(model: TicketLabor): Promise<string> {
    return firstValueFrom(
      this.ticketLaborBillableUpdate
        .mutate({ model: strictMap(UpdateTicketLabor, model) })
        .pipe(map((x) => x.data.supportTicket.ticketLabor.updateBillable))
    );
  }

  updateTicketLaborEstimated(model: TicketLabor): Promise<string> {
    return firstValueFrom(
      this.ticketLaborEstimatedUpdate
        .mutate({ model: strictMap(UpdateTicketLabor, model) })
        .pipe(
          map(
            (x) => x.data.supportTicket.ticketLabor.updateEstimatedWorkingHours
          )
        )
    );
  }

  UpdateTicketRegularOverTimeHours(model: TicketLabor): Promise<string> {
    return firstValueFrom(
      this.ticketRegularOverTimeHours
        .mutate({ model: strictMap(UpdateTicketLabor, model) })
        .pipe(
          map(
            (x) => x.data.supportTicket.ticketLabor.updateRegularOverTimeHours
          )
        )
    );
  }

  updateWorkingHours(model: TicketLabor): Promise<string> {
    return firstValueFrom(
      this.ticketLaborWorkingHoursUpdate
        .mutate({ model: strictMap(UpdateTicketLabor, model) })
        .pipe(map((x) => x.data.supportTicket.ticketLabor.updateWorkingHours))
    );
  }

  removeTicketWorker(model: TicketLabor): Promise<string> {
    return firstValueFrom(
      this.ticketLaborRemove
        .mutate({ model: strictMap(UpdateTicketLabor, model) })
        .pipe(map((x) => x.data.supportTicket.ticketLabor.removeWorker))
    );
  }

  updateTicketLaborHours(model: TicketLaborGridUpdate): Promise<string> {
    return firstValueFrom(
      this.ticketLaborHoursUpdate
        .mutate({ model: strictMap(TicketLaborGridUpdate, model) })
        .pipe(map((x) => x.data.supportTicket.ticketLabor.updateTicketLabor))
    );
  }
}
